import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, PackageOpen, Plus } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, ErrorState, LoadingState, StatusBadge } from "../components/AsyncState";
import { Field, PageHeader } from "../components/PageHeader";
import { formatDate, formatNumber } from "../lib/format";
import type { Unit } from "./UnitsPage";

type Batch = {
  id: string;
  batch_number: string;
  scheduled_date: string;
  quantity: number;
  status: string;
  version: number;
  agent_name: string;
  scope_name: string;
  delivered: number;
  remaining: number;
  no_show: number;
};
type AgentOption = { id: string; name: string; status: string; unit_ids: string[] };
type CycleOption = { id: string; name_ar: string; cycle_code: string; status: string };
type AgentSelf = {
  id: string;
  name: string;
  units: Array<{ id: string; name_ar: string; type: string }>;
};
type CreateResult = { id: string; status: "draft" | "ready"; allocated: number };

export function BatchesPage() {
  const { can, session } = useAuth();
  const isAgent = Boolean(session?.agentId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [scopeUnitId, setScopeUnitId] = useState("");
  const [cycleId, setCycleId] = useState("");
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().slice(0, 10));
  const [quantity, setQuantity] = useState("1");
  const [notes, setNotes] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["batches"],
    queryFn: async () => (await api<Batch[]>("/batches?limit=100")).data
  });
  const agents = useQuery({
    queryKey: ["agents", "active-options"],
    queryFn: async () => (await api<AgentOption[]>("/agents?limit=100&status=active")).data,
    enabled: createOpen && !isAgent
  });
  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => (await api<Unit[]>("/units/tree")).data,
    enabled: createOpen && !isAgent
  });
  const cycles = useQuery({
    queryKey: ["cycles"],
    queryFn: async () => (await api<CycleOption[]>("/cycles")).data,
    enabled: createOpen && !isAgent
  });
  const agentSelf = useQuery({
    queryKey: ["agent-self"],
    queryFn: async () => (await api<AgentSelf>("/agents/me")).data,
    enabled: createOpen && isAgent
  });
  useEffect(() => {
    if (isAgent && !scopeUnitId && agentSelf.data?.units[0]) setScopeUnitId(agentSelf.data.units[0].id);
  }, [agentSelf.data, isAgent, scopeUnitId]);
  const create = useMutation({
    mutationFn: () =>
      api<CreateResult>("/batches", {
        method: "POST",
        body: {
          agentId: isAgent ? undefined : agentId,
          scopeUnitId,
          cycleId: isAgent ? undefined : cycleId || undefined,
          scheduledDate,
          quantity: Number(quantity),
          notes: notes || undefined
        }
      }),
    onSuccess: async (response) => {
      setNotice(
        response.data.status === "ready"
          ? `تم إنشاء الدفعة واختيار ${response.data.allocated} أسرة مستحقة تلقائيًا. الكشف جاهز للمراجعة والطباعة.`
          : "تم إنشاء الدفعة كمسودة. يمكنك مراجعتها ثم تخصيصها."
      );
      setCreateOpen(false);
      setNotes("");
      await queryClient.invalidateQueries({ queryKey: ["batches"] });
      if (isAgent) void navigate(`/batches/${response.data.id}`);
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <div className="page">
      <PageHeader
        title="دفعات التوزيع"
        description="إنشاء الدفعات وتخصيص المستفيدين ومتابعة التسليم حتى الإكمال."
        actions={
          can("distribution.create") ? (
            <button className="button" type="button" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" /> دفعة جديدة
            </button>
          ) : undefined
        }
      />
      {notice ? (
        <div className="success-panel" role="status">
          <PackageOpen aria-hidden="true" /> <p>{notice}</p>
        </div>
      ) : null}
      {createOpen ? (
        <section className="content-card management-form" aria-labelledby="batch-form-title">
          <div className="section-head">
            <div>
              <h2 id="batch-form-title">إنشاء دفعة</h2>
              <p>
                {isAgent
                  ? "أدخل عدد أسطوانات الغاز؛ سيختار الخادم الأسر المستحقة تلقائيًا ضمن نطاق وكالتك."
                  : "لا يتم اختيار الأسر حتى تضغط تخصيص واعتماد داخل الدفعة."}
              </p>
            </div>
          </div>
          <form className="form-grid" onSubmit={submit}>
            {isAgent ? (
              <div className="field">
                <span>الوكيل</span>
                <strong>{agentSelf.data?.name ?? session?.displayName}</strong>
              </div>
            ) : (
              <Field label="الوكيل" htmlFor="batch-agent">
                <select
                  id="batch-agent"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  required
                >
                  <option value="">اختر الوكيل</option>
                  {(agents.data ?? []).map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="نطاق الدفعة" htmlFor="batch-scope" hint="يجب أن يكون ضمن وحدات الوكيل.">
              <select
                id="batch-scope"
                value={scopeUnitId}
                onChange={(e) => setScopeUnitId(e.target.value)}
                required
              >
                <option value="">اختر الوحدة</option>
                {(isAgent
                  ? (agentSelf.data?.units ?? [])
                  : (units.data ?? []).filter((unit) => unit.status === "active")
                ).map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name_ar}
                  </option>
                ))}
              </select>
            </Field>
            {!isAgent ? (
              <Field
                label="دورة الاستحقاق"
                htmlFor="batch-cycle"
                hint="اختيارية، ويجب أن تكون مقفلة وتشمل النطاق."
              >
                <select id="batch-cycle" value={cycleId} onChange={(e) => setCycleId(e.target.value)}>
                  <option value="">بدون دورة محددة</option>
                  {(cycles.data ?? [])
                    .filter((cycle) => cycle.status === "locked")
                    .map((cycle) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.name_ar} ({cycle.cycle_code})
                      </option>
                    ))}
                </select>
              </Field>
            ) : null}
            <Field label="تاريخ التوزيع" htmlFor="batch-date">
              <input
                id="batch-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                required
              />
            </Field>
            <Field label="الكمية" htmlFor="batch-quantity">
              <input
                id="batch-quantity"
                type="number"
                min="1"
                max="10000"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                required
              />
            </Field>
            <Field label="ملاحظات" htmlFor="batch-notes">
              <textarea id="batch-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </Field>
            {create.isError ? (
              <div className="inline-alert form-span" role="alert">
                {formatApiError(create.error)}
              </div>
            ) : null}
            <div className="dialog-actions form-span">
              <button className="button button--secondary" type="button" onClick={() => setCreateOpen(false)}>
                إلغاء
              </button>
              <button className="button" type="submit" disabled={create.isPending}>
                {create.isPending
                  ? "جارٍ الإنشاء والاختيار…"
                  : isAgent
                    ? "إنشاء واختيار المستفيدين"
                    : "إنشاء المسودة"}
              </button>
            </div>
          </form>
        </section>
      ) : null}
      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState error={query.error} retry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <EmptyState title="لا توجد دفعات" description="أنشئ دفعة جديدة بعد إعداد الوكلاء والدورة." />
      ) : (
        <section className="batch-grid">
          {query.data.map((batch) => {
            const percent = batch.quantity ? Math.round((batch.delivered / batch.quantity) * 100) : 0;
            return (
              <Link className="batch-card" to={`/batches/${batch.id}`} key={batch.id}>
                <div className="row-between">
                  <div className="batch-icon">
                    <PackageOpen aria-hidden="true" />
                  </div>
                  <StatusBadge status={batch.status} />
                </div>
                <h2>{batch.batch_number}</h2>
                <p>
                  {batch.agent_name} · {batch.scope_name}
                </p>
                <div className="date-line">
                  <CalendarDays aria-hidden="true" />
                  {formatDate(batch.scheduled_date)}
                </div>
                <div className="progress-label">
                  <span>
                    تم تسليم {formatNumber(batch.delivered)} من {formatNumber(batch.quantity)}
                  </span>
                  <strong>{formatNumber(percent)}٪</strong>
                </div>
                <progress value={batch.delivered} max={batch.quantity}>
                  {percent}%
                </progress>
                <div className="batch-foot">
                  <span>متبقي: {formatNumber(batch.remaining)}</span>
                  <span>لم يحضر: {formatNumber(batch.no_show)}</span>
                </div>
              </Link>
            );
          })}
        </section>
      )}
    </div>
  );
}
